import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Modal, Spin, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { isMobilePlatform, getRandomNumberByRange } from '../../utils/device';
import './index.css';

interface JigsawCaptchaProps {
  /** 是否显示 */
  visible: boolean;
  /** 验证成功回调 */
  onSuccess?: (data?: any) => void;
  /** 验证失败回调 */
  onFail?: () => void;
  /** 关闭回调 */
  onClose?: () => void;
  /** 图片基础URL */
  imageBaseUrl?: string;
}

// 常量定义
const L = 42; // 滑块边长
const R = 10; // 滑块半径
const W = 310; // canvas宽度
const H = 155; // canvas高度
const PI = Math.PI;
const ACTUAL_L = L + R * 2; // 滑块实际边长

const JigsawCaptcha: React.FC<JigsawCaptchaProps> = ({
  visible,
  onSuccess,
  onFail,
  onClose,
  imageBaseUrl = 'http://cdn.upyun.mallimg.shjinjia.com.cn//oak/login/',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const blockRef = useRef<HTMLCanvasElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderMaskRef = useRef<HTMLDivElement>(null);
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [puzzleX, setPuzzleX] = useState(0);
  const [puzzleY, setPuzzleY] = useState(0);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [originX, setOriginX] = useState(0);
  const [originY, setOriginY] = useState(0);
  const [trail, setTrail] = useState<number[]>([]);
  const [sliderLeft, setSliderLeft] = useState(0);
  const [blockLeft, setBlockLeft] = useState(0);
  const [maskWidth, setMaskWidth] = useState(0);
  const [status, setStatus] = useState<'default' | 'active' | 'success' | 'fail'>('default');
  
  // 使用 ref 存储最新的值，避免闭包问题
  const trailRef = useRef<number[]>([]);
  const blockLeftRef = useRef<number>(0);

  // 获取随机图片URL
  const getRandomImg = useCallback(() => {
    return `${imageBaseUrl}${getRandomNumberByRange(0, 50)}.png`;
  }, [imageBaseUrl]);

  // 绘制拼图块
  const drawPuzzle = useCallback(
    (ctx: CanvasRenderingContext2D, operation: 'fill' | 'clip', x: number, y: number) => {
      ctx.beginPath();
      // left
      ctx.moveTo(x, y);
      // top
      ctx.lineTo(x + L / 2, y);
      ctx.arc(x + L / 2, y - R + 2, R, 0, 2 * PI);
      // right
      ctx.lineTo(x + L / 2, y);
      ctx.lineTo(x + L, y);
      ctx.lineTo(x + L, y + L / 2);
      ctx.arc(x + L + R - 2, y + L / 2, R, 0, 2 * PI);
      ctx.lineTo(x + L, y + L / 2);
      ctx.lineTo(x + L, y + L);
      ctx.lineTo(x, y + L);
      ctx.lineTo(x, y);
      // 修饰
      ctx.fillStyle = '#fff';
      ctx[operation]();
      ctx.beginPath();
      ctx.arc(x, y + L / 2, R, 1.5 * PI, 0.5 * PI);
      ctx.globalCompositeOperation = 'xor';
      ctx.fill();
    },
    []
  );

  // 初始化图片（绘制背景和拼图块）
  const initImage = useCallback(() => {
    if (!canvasRef.current || !blockRef.current) return;

    const canvas = canvasRef.current;
    const block = blockRef.current;
    const canvasCtx = canvas.getContext('2d');
    const blockCtx = block.getContext('2d');

    if (!canvasCtx || !blockCtx) return;

    setLoading(true);

    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      if (!canvasCtx || !blockCtx) return;

      // 随机生成拼图位置（与老项目保持一致的范围）
      const x = getRandomNumberByRange(ACTUAL_L + 10, W - (ACTUAL_L + 10));
      const y = getRandomNumberByRange(10 + R * 2, H - (ACTUAL_L + 10));

      setPuzzleX(x);
      setPuzzleY(y);

      // 先在 blockCtx 上设置裁剪区域（clip），然后再绘制图片，
      // 这样只有拼图形状内的区域会被绘制出来，效果与老项目一致。
      // 注意：clip 只影响后续的绘制，所以必须在 drawImage 之前调用。
      drawPuzzle(blockCtx, 'clip', x, y);

      // 在主 canvas 上绘制背景和缺口轮廓
      canvasCtx.clearRect(0, 0, W, H);
      canvasCtx.drawImage(img, 0, 0, W, H);
      drawPuzzle(canvasCtx, 'fill', x, y);

      // 在 block canvas 上绘制拼图块（已被 clip 裁剪成拼图形状）
      blockCtx.clearRect(0, 0, W, H);
      blockCtx.drawImage(img, 0, 0, W, H);

      // 获取拼图块图像数据
      const yOffset = y - R * 2 + 2;
      const imageData = blockCtx.getImageData(x, yOffset, ACTUAL_L, ACTUAL_L);
      block.width = ACTUAL_L;
      blockCtx.putImageData(imageData, 0, yOffset);

      setLoading(false);
      imgRef.current = img;
    };

    img.onerror = () => {
      // 图片加载失败，重新加载
      img.src = getRandomImg();
    };

    img.src = getRandomImg();
  }, [drawPuzzle, getRandomImg]);

  // 重置验证码
  const reset = useCallback(() => {
    if (!canvasRef.current || !blockRef.current) return;

    const canvasCtx = canvasRef.current.getContext('2d');
    const blockCtx = blockRef.current.getContext('2d');

    if (!canvasCtx || !blockCtx) return;

    // 清空canvas
    canvasCtx.clearRect(0, 0, W, H);
    blockCtx.clearRect(0, 0, W, H);
    blockRef.current.width = W;

    // 重置状态
    setSliderLeft(0);
    setBlockLeft(0);
    setMaskWidth(0);
    setStatus('default');
    setTrail([]);
    setIsMouseDown(false);
    trailRef.current = [];
    blockLeftRef.current = 0;

    // 更新文本
    if (textRef.current) {
      textRef.current.textContent = '向右滑动滑块填充拼图';
    }

    // 重新初始化
    setTimeout(() => {
      initImage();
    }, 100);
  }, [initImage]);

  // 验证
  const verify = useCallback(
    (currentTrail: number[], currentBlockLeft: number) => {
      if (currentTrail.length === 0) {
        return { spliced: false, turingTest: false };
      }

      // 计算平均值
      const sum = currentTrail.reduce((a, b) => a + b, 0);
      const average = sum / currentTrail.length;

      // 计算标准差
      const deviations = currentTrail.map((x) => x - average);
      const variance = deviations.reduce((sum, d) => sum + d * d, 0) / currentTrail.length;
      const stddev = Math.sqrt(variance);

      // 验证位置是否匹配（允许10px误差）
      const spliced = Math.abs(currentBlockLeft - puzzleX) < 10;
      // 验证是否为人为操作（标准差不为0表示有移动轨迹）
      const turingTest = stddev !== 0 && average !== 0;

      return { spliced, turingTest };
    },
    [puzzleX]
  );

  // 处理鼠标/触摸开始
  const handleStart = useCallback((e: MouseEvent | TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

    setOriginX(clientX);
    setOriginY(clientY);
    setIsMouseDown(true);
    setStatus('active');
    setTrail([]); // 重置轨迹
    trailRef.current = []; // 重置 ref
    e.preventDefault();
  }, []);

  // 处理鼠标/触摸移动
  const handleMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isMouseDown) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

      const moveX = clientX - originX;
      const moveY = clientY - originY;

      // 限制滑块移动范围
      if (moveX < 0 || moveX + 38 >= W) return;

      const newBlockLeft = ((W - 40 - 20) / (W - 40)) * moveX;
      setSliderLeft(moveX);
      setBlockLeft(newBlockLeft);
      setMaskWidth(moveX);
      trailRef.current = [...trailRef.current, moveY];
      blockLeftRef.current = newBlockLeft;
      setTrail([...trailRef.current]);

      e.preventDefault();
    },
    [isMouseDown, originX, originY]
  );

  // 处理鼠标/触摸结束
  const handleEnd = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isMouseDown) return;

      const clientX =
        'changedTouches' in e ? e.changedTouches[0].clientX : (e as MouseEvent).clientX;

      if (clientX === originX) {
        setIsMouseDown(false);
        setStatus('default');
        return;
      }

      setIsMouseDown(false);
      setStatus('default');

      // 使用 ref 中的最新值进行验证
      const result = verify(trailRef.current, blockLeftRef.current);

      if (result.spliced) {
        if (result.turingTest) {
          // 验证成功
          setStatus('success');
          if (textRef.current) {
            textRef.current.textContent = '验证成功';
          }
          // 将拖动轨迹数据传递给上层（深拷贝一份，避免后续被修改）
          const traceData = [...trailRef.current];
          setTimeout(() => {
            onSuccess?.(traceData);
          }, 500);
        } else {
          // 可能是机器人操作
          setStatus('fail');
          if (textRef.current) {
            textRef.current.textContent = '再试一次';
          }
          message.warning('检测到异常操作，请重试');
          setTimeout(() => {
            reset();
          }, 1500);
        }
      } else {
        // 位置不匹配
        setStatus('fail');
        if (textRef.current) {
          textRef.current.textContent = '再试一次';
        }
        message.error('验证失败，请重试');
        onFail?.();
        setTimeout(() => {
          reset();
        }, 1500);
      }
    },
    [isMouseDown, originX, verify, onSuccess, onFail, reset]
  );

  // 绑定事件
  useEffect(() => {
    if (!visible || !sliderRef.current) return;

    const slider = sliderRef.current;
    const isMobile = isMobilePlatform();

    const touchStartEvent = isMobile ? 'touchstart' : 'mousedown';
    const touchMoveEvent = isMobile ? 'touchmove' : 'mousemove';
    const touchEndEvent = isMobile ? 'touchend' : 'mouseup';

    const startHandler = handleStart as EventListener;
    const moveHandler = handleMove as EventListener;
    const endHandler = handleEnd as EventListener;

    slider.addEventListener(touchStartEvent, startHandler);
    document.addEventListener(touchMoveEvent, moveHandler);
    document.addEventListener(touchEndEvent, endHandler);

    return () => {
      slider.removeEventListener(touchStartEvent, startHandler);
      document.removeEventListener(touchMoveEvent, moveHandler);
      document.removeEventListener(touchEndEvent, endHandler);
    };
  }, [visible, handleStart, handleMove, handleEnd]);

  // 初始化
  useEffect(() => {
    if (visible && containerRef.current) {
      reset();
    }
  }, [visible, reset]);

  // 防止文本选择
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.onselectstart = () => false;
    }
  }, []);

  return (
    <Modal
      title="人机验证"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={360}
      centered
      maskClosable={false}
      closable={true}
      className="jigsaw-captcha-modal"
    >
      <div ref={containerRef} className="jigsaw-captcha-container">
        <Spin spinning={loading} tip="加载验证码中...">
          <div className="captcha-wrapper">
            <canvas ref={canvasRef} width={W} height={H} className="captcha-canvas" />
            <canvas
              ref={blockRef}
              width={W}
              height={H}
              className="captcha-block"
              // 将整块 canvas 横向移动，对齐到缺口位置
              style={{ left: `${blockLeft}px` }}
            />
            <div
              className="refresh-icon"
              onClick={reset}
              title="刷新验证码"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  reset();
                }
              }}
            >
              <ReloadOutlined />
            </div>
          </div>

          <div
            ref={sliderContainerRef}
            className={`slider-container slider-container-${status}`}
          >
            <div ref={sliderMaskRef} className="slider-mask" style={{ width: `${maskWidth}px` }} />
            <div
              ref={sliderRef}
              className="slider"
              style={{ left: `${sliderLeft}px` }}
              role="button"
              tabIndex={0}
            >
              <span className="slider-icon" />
            </div>
            <span ref={textRef} className="slider-text">
              向右滑动滑块填充拼图
            </span>
          </div>
        </Spin>
      </div>
    </Modal>
  );
};

export default JigsawCaptcha;

